import { useFormatters } from '@/hooks/useFormatters';
import { useSuspenseQuery } from '@apollo/client';
import ApolloErrorBoundary from '@components/ApolloErrorBoundary';
import NumericTextField from '@components/NumericTextField';
import { formatPosition } from '@features/gateCommon/utils/formatPosition';
import { msg, t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Add, Remove } from '@mui/icons-material';
import { Box, Button, IconButton, Skeleton, Typography } from '@mui/material';
import { Suspense, useEffect, useRef, type FC, type ReactNode } from 'react';
import {
  AU_BTN_GHOST_INFO_SM_SX,
  AU_NUMBER_FIELD_SX,
  AU_NUMBER_STEPPER_SX,
  AU_NUMBER_UNIT_SX,
  OC_DEFINE_ERROR_SX,
  OC_DEFINE_FIELD_LABEL_SX,
  OC_DEFINE_FIELD_SX,
  POSITION_TONE_COLOR,
  auToneSurfaceSx,
  type PositionTone,
} from '@/theme/au-compat';
import { GET_GATE_LIMITS_DATA } from '../graphql/queries/getMoveGateModalData';
import type { FirmwareLimits } from '../hooks/useGateLiveStatus';
import { AwaitingDevice } from './AwaitingDevice';
import { PositionSlider } from './Slider';

interface Props {
  gateId: string | null;
  setIsValid: (isValid: boolean) => void;
  position: string;
  setPosition: (value: string) => void;
  setInitialPosition: (value: number) => void;
  /** Mandan sobre `dataConfiguration`: son los que el equipo aplica. `null` sin lectura. */
  firmwareLimits?: FirmwareLimits | null;
  /** Posición que reportó el equipo; manda sobre `lastKnownPosition`. */
  livePosition?: number | null;
  /** Primera consulta al equipo: el formulario todavía no tiene de dónde partir. */
  awaitingDevice?: boolean;
  /** Refresco en curso: el formulario sigue armado y no pisa lo que el usuario escribió. */
  refreshing?: boolean;
}

const SuspenseComponent: FC<Omit<Props, 'gateId'> & { gateId: string }> = ({
  gateId,
  setIsValid,
  position,
  setPosition,
  setInitialPosition,
  firmwareLimits,
  livePosition,
  awaitingDevice,
  refreshing,
}) => {
  const { i18n, _ } = useLingui();
  const { formatMetricValue } = useFormatters();
  const formatLimit = (value: number | null | undefined) =>
    value == null ? '—' : formatPosition(value, formatMetricValue);
  const { data } = useSuspenseQuery(GET_GATE_LIMITS_DATA, {
    fetchPolicy: 'no-cache',
    variables: { gateId },
  });
  const defaultPosition = livePosition ?? data.gate.movements2.lastKnownPosition?.point ?? 0;
  const { minPosition, maxPosition } = data.gate.dataConfiguration;
  let min = firmwareLimits?.min ?? minPosition ?? 0;
  let max = firmwareLimits?.max ?? maxPosition ?? null;
  // Un límite del equipo con el otro de la base puede no cerrar un recorrido. El del equipo es el
  // que se aplica, así que se descarta el de la base y se avisa que no hay rango confirmado.
  const limitsConflict = max !== null && min > max;
  if (limitsConflict) {
    if (firmwareLimits?.min != null) max = null;
    else min = 0;
  }
  // Divergencia sólo contra un límite configurado: sin configuración no hay nada con qué discrepar.
  const minDiverges =
    firmwareLimits?.min != null && minPosition != null && minPosition !== firmwareLimits.min;
  const maxDiverges =
    firmwareLimits?.max != null && maxPosition != null && maxPosition !== firmwareLimits.max;
  const limitsDiverge = minDiverges || maxDiverges;

  // Última posición sembrada: si el campo todavía la tiene, el usuario no lo tocó y se puede
  // re-sembrar; si no, lo que escribió se respeta.
  const seededRef = useRef<string | null>(null);
  useEffect(() => {
    if (awaitingDevice || refreshing) return;
    setInitialPosition(defaultPosition);
    if (seededRef.current === null || position === '' || position === seededRef.current) {
      setPosition(String(defaultPosition));
    }
    seededRef.current = String(defaultPosition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPosition, awaitingDevice, refreshing]);

  /**
   * Estado derivado `idle|dirty|error` (DPG `validate.ts`) — `min`/`max` se tratan por
   * nulabilidad, nunca por truthiness, para que un límite en 0 sea real; y la posición actual
   * es inválida sin error (no hay nada que operar, no hay nada mal), en vez de compartir la
   * rama de "vacío" solo porque `0` es falsy.
   *
   * El string se guarda **ya normalizado** (ver `onChangePosition`), así que acá alcanza un
   * `parseFloat` directo y —lo que importa— el padre lee el mismo número: `MoveGateModal` hace
   * `parseFloat(position)` para la mutation y `Number(position)` para el texto de la confirmación, y
   * sobre un `"60,5"` crudo eso daba `60` y `NaN` respectivamente. Normalizar sólo acá habría dejado
   * al hijo leyendo 60,5 y al padre 60.
   */
  const parsed = position === '' ? NaN : parseFloat(position);
  const value = isNaN(parsed) ? null : parsed;
  const isDirty = value !== null && value !== defaultPosition;

  let error = '';
  if (value !== null) {
    if (value < min) {
      error = t(i18n)`La posición es menor al mínimo permitido`;
    } else if (max !== null && value > max) {
      error = t(i18n)`La posición es mayor al máximo permitido`;
    }
  }
  const isValid = isDirty && error === '';

  // La precedencia es `error > dirty > idle`, la de `.oc-define-field[data-tone]`.
  const tone: PositionTone = error ? 'error' : isDirty ? 'dirty' : 'idle';
  const toneColor = POSITION_TONE_COLOR[tone];

  useEffect(() => {
    setIsValid(isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  /**
   * Coma → punto **al guardar**, no al leer.
   *
   * `NumericTextField` ya hace este reemplazo, pero recién en su `onBlur`: mientras se escribe, el
   * estado tiene la coma cruda y todo lo que la lea ve otro número — `parseFloat("60,5")` es `60` y
   * `Number("60,5")` es `NaN`. Y `position` lo leen **dos** componentes: este formulario para el tono
   * y el slider, y `MoveGateModal` para la mutation y para el texto de la confirmación. Normalizar en
   * el punto de escritura es lo que garantiza que los dos lean el mismo número, en vez de repetir el
   * reemplazo en cada lectura y que una se olvide.
   *
   * Consecuencia visible: quien escribe con coma decimal ve el punto en el campo de inmediato en vez
   * de al salir de él. Es el mismo valor final que el campo ya producía en `onBlur`, sólo antes.
   */
  const onChangePosition = (newValue: string) => {
    setPosition(newValue.replace(',', '.'));
  };

  const onResetPosition = () => {
    setPosition(String(defaultPosition));
  };

  // El campo puede mostrar un valor fuera de rango para dar feedback (`position` sin acotar);
  // el slider se mantiene acotado al rango real.
  let sliderValue = value ?? defaultPosition;
  if (sliderValue < min) sliderValue = min;
  if (max !== null && sliderValue > max) sliderValue = max;

  // Montado mientras se espera para que la consulta a la base corra en paralelo con la del equipo.
  if (awaitingDevice) return <FormSkeleton caption={<AwaitingDevice />} />;

  return (
    <Box sx={OC_DEFINE_FIELD_SX}>
      <Typography sx={OC_DEFINE_FIELD_LABEL_SX}>
        <Trans>Posición solicitada</Trans>
      </Typography>
      {refreshing && (
        <Typography sx={{ fontSize: 12 }}>
          <AwaitingDevice />
        </Typography>
      )}

      {/* `.oc-define-field-row` — el grupo numérico mide lo suyo y el hueco empuja «Restablecer»
          contra el borde derecho, igual que en el espécimen: ahí el `flex: 1` está en el *root* del
          `NumberField`, no en su grupo, que es `inline-flex`. */}
      <Box display="flex" alignItems="center" gap="10px">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <NumericTextField
            error={Boolean(error)}
            value={String(position)}
            onChange={(event) => onChangePosition(event.target.value)}
            onBlur={(event) => {
              // Vaciar y salir del campo vuelve a la **posición actual**, no a `0`. Con `0` y una
              // compuerta cuyo `minPosition` es mayor que cero, salía un «menor al mínimo» que el
              // usuario no provocó: antes de tratar los límites por nulabilidad el `0` caía en la
              // rama falsy y nunca llegaba a compararse, así que el error apareció junto con ese fix.
              // La posición actual es el estado neutro del formulario — inválida, pero sin error.
              //
              // Se lee `event.target.value` y no `position`: `NumericTextField` normaliza en su
              // `onBlur` —un texto que no parsea («-», «.») pasa a `''` vía `onChange`— y recién
              // después llama a este handler, que con `position` vería el valor de antes de vaciarse
              // y dejaría el campo en blanco con el botón deshabilitado y sin error.
              if (!event.target.value) onChangePosition(String(defaultPosition));
            }}
            sx={AU_NUMBER_FIELD_SX}
            inputProps={{ style: { color: toneColor } }}
            InputProps={{
              // `.au-number-btn` — los dos steppers de `NumberField`, con el `step: 1` del
              // espécimen. No acotan al rango: el campo tiene que poder salirse para dar feedback,
              // igual que al escribir (es el slider el que se mantiene dentro).
              startAdornment: (
                <IconButton
                  aria-label={_(msg`Restar`)}
                  onClick={() => onChangePosition(String((value ?? defaultPosition) - 1))}
                  sx={AU_NUMBER_STEPPER_SX}
                >
                  <Remove />
                </IconButton>
              ),
              endAdornment: (
                <>
                  {/* La unidad se tiñe con el número: en la capa la regla de tono alcanza al
                      `input` y al `span` de la unidad, porque son el mismo dato. */}
                  <Typography sx={{ ...AU_NUMBER_UNIT_SX, color: toneColor }}>cm</Typography>
                  <IconButton
                    aria-label={_(msg`Sumar`)}
                    onClick={() => onChangePosition(String((value ?? defaultPosition) + 1))}
                    sx={AU_NUMBER_STEPPER_SX}
                  >
                    <Add />
                  </IconButton>
                </>
              ),
            }}
          />
        </Box>
        {isDirty && (
          <Button onClick={onResetPosition} sx={{ ...AU_BTN_GHOST_INFO_SM_SX, flexShrink: 0 }}>
            <Trans>Restablecer</Trans>
          </Button>
        )}
      </Box>

      {error && <Typography sx={OC_DEFINE_ERROR_SX}>{error}</Typography>}

      {limitsConflict ? (
        <Typography role="note" sx={{ ...auToneSurfaceSx('warning'), fontSize: 12 }}>
          <Trans>
            El límite que reporta el equipo ({formatLimit(firmwareLimits?.min)} a{' '}
            {formatLimit(firmwareLimits?.max)}) no es compatible con el configurado en la plataforma
            ({formatLimit(minPosition)} a {formatLimit(maxPosition)}): se usa sólo el del equipo y
            no hay un rango confirmado.
          </Trans>
        </Typography>
      ) : (
        limitsDiverge && (
          <Typography role="note" sx={{ ...auToneSurfaceSx('warning'), fontSize: 12 }}>
            <Trans>
              Los límites del equipo ({formatLimit(firmwareLimits?.min)} a{' '}
              {formatLimit(firmwareLimits?.max)}) difieren de los configurados en la plataforma (
              {formatLimit(minPosition)} a {formatLimit(maxPosition)}). Se usan los que reportó el
              equipo.
            </Trans>
          </Typography>
        )
      )}

      {/* Sin máximo no hay recorrido que dibujar (MUI asumiría 100): queda sólo el campo. */}
      {max !== null && (
        <PositionSlider
          tone={tone}
          value={sliderValue}
          onChange={(newValue) => onChangePosition(String(newValue))}
          min={min}
          max={max}
        />
      )}
    </Box>
  );
};

const FormSkeleton: FC<{ caption?: ReactNode }> = ({ caption }) => (
  <Box sx={OC_DEFINE_FIELD_SX}>
    <Typography sx={OC_DEFINE_FIELD_LABEL_SX}>
      <Trans>Posición solicitada</Trans>
    </Typography>
    {caption && <Typography sx={{ fontSize: 12 }}>{caption}</Typography>}

    <Skeleton variant="rounded">
      <NumericTextField defaultValue="300" sx={AU_NUMBER_FIELD_SX} />
    </Skeleton>
    <Skeleton variant="rounded" width="100%">
      <PositionSlider tone="idle" value={0} onChange={() => {}} min={0} max={100} />
    </Skeleton>
  </Box>
);

export const MoveGateForm: FC<Props> = (props) => {
  if (!props.gateId) return null;
  const newProps = {
    ...props,
    gateId: props.gateId,
  };

  return (
    <ApolloErrorBoundary>
      <Suspense
        fallback={<FormSkeleton caption={props.awaitingDevice ? <AwaitingDevice /> : undefined} />}
      >
        <SuspenseComponent {...newProps} />
      </Suspense>
    </ApolloErrorBoundary>
  );
};
